import { ExpressionValue, OneOfElements } from '@shared/models/project.model';
import { IElementExpressionProperty } from '@shared/models/element-property.model';
import {
  getElementProperties,
  isElementExpressionProperty,
} from '../../element-property.helpers';
import { isOneOfPropertyElements } from '../../recursive-element.helpers';
import { ProjectChangeType } from '../update-engine.models';
import type { ProjectChangeMap } from '../update-engine.models';

type PropertyExpressionChange =
  ProjectChangeMap[ProjectChangeType.ElementPropertyExpression];

/**
 * Build a partial element change for ElementPropertyExpression.
 *
 * @param element - Current element state
 * @param change - Expression change payload
 * @returns Partial<OneOfElements> or undefined
 */
export const elementPropertyExpressionToChange = (
  element: OneOfElements,
  change: PropertyExpressionChange,
): Partial<OneOfElements> | undefined => {
  if (!isOneOfPropertyElements(element)) return undefined;

  const props = getElementProperties(element);
  const idx = props.findIndex(
    (p) => isElementExpressionProperty(p) && p.name === change.propertyName,
  );
  if (idx === -1) return undefined;

  const prop = props[idx] as IElementExpressionProperty | undefined;
  if (!prop) return undefined;

  const countField = prop.count
    ? 'count'
    : prop.fallbackCount
      ? 'fallbackCount'
      : undefined;
  if (!countField) return undefined;

  const base = prop[countField] as ExpressionValue;
  if (base.expression === change.expression) return undefined;

  const newProps = [...props];
  newProps[idx] = {
    ...prop,
    [countField]: { ...base, expression: change.expression },
  };

  return { properties: newProps };
};
